import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { CheckCircle2, Loader2, UploadCloud, XCircle } from 'lucide-react'
import { useFuentes } from '@/modules/bd-unificada/hooks/useFuentes'
import { useJobStatus, useSubirArchivo } from '@/modules/bd-unificada/hooks/useUploads'

interface UploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function UploadDialog({ open, onOpenChange }: UploadDialogProps) {
  const [fuenteId, setFuenteId] = useState('')
  const [archivo, setArchivo] = useState<File | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)

  const { data: fuentes } = useFuentes()
  const subir = useSubirArchivo()
  const { data: job } = useJobStatus(jobId)

  const procesando = job && (job.estado === 'pending' || job.estado === 'processing')
  const terminado = job && (job.estado === 'done' || job.estado === 'error')

  const handleSubir = async () => {
    if (!archivo || !fuenteId) return
    const res = await subir.mutateAsync({ archivo, fuenteId })
    setJobId(res.job_id)
  }

  const handleClose = (v: boolean) => {
    if (!v) {
      setFuenteId('')
      setArchivo(null)
      setJobId(null)
    }
    onOpenChange(v)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Cargar archivo de contactos</DialogTitle>
          <DialogDescription>
            Sube un archivo CSV / XLSX / XLS / TXT asociado a una fuente. El procesamiento ocurre
            en segundo plano (máx. 50 MB).
          </DialogDescription>
        </DialogHeader>

        {!jobId && (
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Fuente</Label>
              <Select value={fuenteId} onValueChange={setFuenteId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una fuente" />
                </SelectTrigger>
                <SelectContent>
                  {fuentes?.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.nombre} (prioridad {f.prioridad})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="archivo">Archivo</Label>
              <Input
                id="archivo"
                type="file"
                accept=".csv,.xlsx,.xls,.txt"
                onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
        )}

        {jobId && (
          <div className="space-y-3 py-2">
            {procesando && (
              <div className="flex items-center gap-2 text-sm">
                <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                Procesando archivo… (estado: {job?.estado})
              </div>
            )}
            {job?.estado === 'done' && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-green-600">
                  <CheckCircle2 className="h-5 w-5" /> Procesamiento completado
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Total: <b>{job.total}</b></div>
                  <div>Nuevos: <b>{job.nuevos}</b></div>
                  <div>Actualizados: <b>{job.actualizados}</b></div>
                  <div>A la cola: <b>{job.cola}</b></div>
                  <div>Errores: <b>{job.errores}</b></div>
                </div>
                {!!job.detalle_errores?.length && (
                  <div className="max-h-40 overflow-y-auto rounded-md border border-gray-300 dark:border-gray-700 p-2 text-xs">
                    {job.detalle_errores.map((e, i) => (
                      <div key={i} className="text-muted-foreground">
                        {e.fila != null ? `Fila ${e.fila}: ` : ''}{e.razon}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {job?.estado === 'error' && (
              <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                <XCircle className="h-5 w-5" /> El procesamiento falló.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!jobId && (
            <>
              <Button type="button" variant="outline" onClick={() => handleClose(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleSubir}
                disabled={!archivo || !fuenteId || subir.isPending}
                className="bg-blue-800 hover:bg-blue-900 text-white"
              >
                {subir.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <UploadCloud className="mr-2 h-4 w-4" />
                )}
                Subir
              </Button>
            </>
          )}
          {terminado && (
            <Button onClick={() => handleClose(false)} className="bg-blue-800 hover:bg-blue-900 text-white">
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
